import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'

export type FormData = {
  title: string
  firstName: string
  lastName: string
  email: string
  gender: GraphQL.Gender
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useGuestForm({ id, onDone }: Options) {
  const { t } = useLingui()

  const [createGuest] = useMutation(AdminAPI.guest.CREATE, {
    refetchQueries: [
      AdminAPI.guest.QUERY,
      AdminAPI.stay.GET,
      AdminAPI.stay.GET_BY_TOKEN
    ]
  })

  const [updateGuest] = useMutation(AdminAPI.guest.UPDATE, {
    refetchQueries: [AdminAPI.stay.GET, AdminAPI.stay.GET_BY_TOKEN]
  })

  const { parseApolloErrors, errors } = Form.useErrorHelpers({
    mapper(field, message) {
      if (field === 'email' && message === 'has already been taken') {
        return [
          field,
          {
            type: message,
            message: t`A guest with this email exists already.`
          }
        ]
      }
    }
  })

  const create = useCallback(
    async (data: FormData) => {
      try {
        const response = await createGuest({
          variables: {
            input: data
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [onDone, parseApolloErrors, createGuest]
  )

  const update = useCallback(
    async (data: FormData) => {
      if (!id) return

      try {
        const response = await updateGuest({
          variables: {
            input: {
              id,
              ...data
            }
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [id, onDone, updateGuest, parseApolloErrors]
  )

  return {
    errors,
    create,
    update
  }
}
